import ky from 'ky';
import { http, ADMIN_PREFIX, normalizePage, tokenStorage } from '@shared/api';
import { env } from '@shared/config/env';
import { decodeJwt } from '@shared/lib/jwt';
import type { Page, UUID, AuthTokens } from '@shared/api';
import type {
  AcceptInvitePayload,
  AdminInviteStatus,
  AdminInviteView,
  AdminUserView,
  CreateInvitePayload,
} from './types';

interface RawAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  user?: { id: UUID; email: string; displayName: string | null };
}

export const adminApi = {
  list: () => http.get('admins').json<AdminUserView[]>(),

  invite: (body: CreateInvitePayload) =>
    http.post('admins/invite', { json: body }).json<AdminInviteView>(),

  listInvites: async (
    status: AdminInviteStatus = 'PENDING',
    page = 0,
    size = 50,
  ): Promise<Page<AdminInviteView>> => {
    const raw = await http
      .get('admins/invites', { searchParams: { status, page, size } })
      .json<unknown>();
    return normalizePage<AdminInviteView>(raw);
  },

  revokeInvite: (id: UUID) => http.delete(`admins/invites/${id}`).json<void>(),

  revokeAdmin: (id: UUID) => http.delete(`admins/${id}`).json<void>(),
};

export async function acceptInvite(payload: AcceptInvitePayload): Promise<{
  tokens: AuthTokens;
  identity: { id: UUID; email: string; displayName: string | null };
}> {
  const url = `${env.apiBaseUrl}${ADMIN_PREFIX}/auth/invite/accept`;
  const raw = await ky
    .post(url, {
      json: payload,
      retry: 0,
      // Defensive: invite/accept is anonymous — never carry a stale Bearer.
      hooks: {
        beforeRequest: [(req) => req.headers.delete('Authorization')],
      },
    })
    .json<RawAuthResponse>();
  const tokens = {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresIn: raw.expiresIn,
    refreshExpiresIn: raw.refreshExpiresIn,
  };
  tokenStorage.set(tokens);

  if (raw.user) {
    return {
      tokens,
      identity: {
        id: raw.user.id,
        email: raw.user.email,
        displayName: raw.user.displayName,
      },
    };
  }

  const sub = decodeJwt(raw.accessToken)?.sub ?? '';
  let identity = { id: sub, email: '', displayName: null as string | null };
  if (sub) {
    try {
      const basic = await http
        .get(`users/${sub}/basic`)
        .json<{ id: string; email: string | null; displayName: string }>();
      identity = { id: basic.id, email: basic.email ?? '', displayName: basic.displayName };
    } catch {
      /* keep JWT-derived fallback */
    }
  }
  return { tokens, identity };
}
